import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { readSheet } from 'read-excel-file/browser';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, Stack, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Upload, Send, ShieldCheck, Trash2, Save as SaveIcon, RotateCcw } from 'lucide-react';
import { RUNNERSHUB_URLS } from '../../config/urls';
import {
  buildLocalizedMmcMarkup, buildMmcEmailHtml, containsMarketingSections,
  getMmcSubject, MmcParticipant, ParsedMmcData, markupToPlainText, parseMmcCsv,
  parseMmcRows, removeMarketingSections,
} from '../../utils/mmcEmailPoc';
import {
  loadMmcContentTemplate, PreparedMmcEmail, queueMmcEmails, queueMmcTestEmail,
  saveMmcContentTemplate,
} from '../../services/mmcEmailPocService';

const NORWEGIAN_BODY = `Det er deltakerne som gjør MMC til den møteplassen og opplevelsen vi ønsker at løpet skal være.

## Resultatlister

De offisielle resultatlistene finner du her:

[Se resultatlistene](https://www.racetracker.one/2026/meraker-mountain-challenge/results?lang=no)

Dersom du oppdager feil eller mangler, setter vi stor pris på at du gir oss beskjed på **post@mmctrail.no**, så retter vi opp så raskt som mulig.

**NB:** En del etteranmeldte i **2K** står dessverre oppført som **N.N.** Dette skyldes at vi i innspurten på Teveltunet ikke rakk å registrere alle deltakerne fullt ut før start. Gi oss gjerne beskjed dersom dette gjelder deg.

## Bilder fra arrangementet

Vår fotograf Matthias Wendling har gjort sine bilder fra arrangementet tilgjengelige her:

[Se bildene](https://www.picdrop.com/matthiaswendling/6FFsRTDgzP)

Vi kommer også til å publisere flere bilder og små tilbakeblikk på våre sosiale medier:

- Instagram: [Meråker Mountain Challenge](https://www.instagram.com/merakermountainc/)
- Facebook: [Meråker Mountain Challenge – MMC](https://www.facebook.com/search/top?q=mer%C3%A5ker%20mountain%20challenge%20-%20mmc)

## Påmelding til MMC 2027

Planleggingen av neste års arrangement er allerede i gang, og påmeldingen er åpnet.

[Informasjon og påmelding til MMC 2027](https://www.racetracker.one/2027/meraker-mountain-challenge?lang=no)

Vi håper å se mange av dere tilbake i Meråker neste år!

## 25 % rabatt hos 226ERS

Vår samarbeidspartner **226ERS Norge** ønsker å takke alle deltakerne med en rabattkode.

**Rabattkode: 5XZ7J2DB**

Koden gir **25 % rabatt** hos [226ERS Norge](https://www.226ers.no/).

Vilkår:
- Gyldig til og med **30. september 2026**.
- Kan brukes **én gang per bruker**.
- Kan ikke kombineres med andre rabattkuponger.

Til slutt vil vi rette en stor takk til alle frivillige, sponsorer, samarbeidspartnere, værguder og alle som var med på å gjøre Meråker Mountain Challenge 2026 til en minnerik helg.

Vi håper å se deg eller dere igjen i Meråker i 2027!

Med vennlig hilsen

**Meråker Mountain Challenge**`;

const ENGLISH_BODY = `It is the participants who make MMC the meeting place and experience we want the event to be.

## Results

The official results are available here:

[View the results](https://www.racetracker.one/2026/meraker-mountain-challenge/results?lang=en)

If you notice an error or omission, please let us know at **post@mmctrail.no**, and we will correct it as soon as possible.

**Please note:** Some late entries in the **2K** race are unfortunately listed as **N.N.** During the final preparations at Teveltunet, we did not have time to complete every registration before the start. Please contact us if this applies to you.

## Photos from the event

Our photographer Matthias Wendling has made his photos from the event available here:

[View the photos](https://www.picdrop.com/matthiaswendling/6FFsRTDgzP)

We will also publish more photos and highlights on social media:

- Instagram: [Meråker Mountain Challenge](https://www.instagram.com/merakermountainc/)
- Facebook: [Meråker Mountain Challenge – MMC](https://www.facebook.com/search/top?q=mer%C3%A5ker%20mountain%20challenge%20-%20mmc)

## Registration for MMC 2027

Planning for next year's event is already under way, and registration is open.

[Information and registration for MMC 2027](https://www.racetracker.one/2027/meraker-mountain-challenge?lang=en)

We hope to welcome many of you back to Meråker next year!

## 25 % discount at 226ERS

Our partner **226ERS Norway** would like to thank all participants with a discount code.

**Discount code: 5XZ7J2DB**

The code gives a **25 % discount** at [226ERS Norway](https://www.226ers.no/).

Terms:
- Valid through **30 September 2026**.
- May be used **once per customer**.
- Cannot be combined with other discount codes.

Finally, we would like to thank all volunteers, sponsors, partners, the weather gods and everyone who helped make Meråker Mountain Challenge 2026 a memorable weekend.

We hope to see you in Meråker again in 2027!

Kind regards,

**Meråker Mountain Challenge**`;

const LOGO_URL = `${RUNNERSHUB_URLS.main}/images/mmc-logo-red.png`;
const outcomeLabel = (outcome: MmcParticipant['outcome']): string => ({ finished: 'Fullført', dns: 'DNS', dnf: 'DNF', mixed: 'Blandet' }[outcome]);

const LOCAL_TEMPLATE_KEY = 'runnershub:mmc-email-poc:content:v1';

interface LocalTemplateDraft {
  norwegianBody: string;
  englishBody: string;
  updatedAt: number;
}

const readLocalTemplateDraft = (): LocalTemplateDraft | null => {
  try {
    const raw = window.localStorage.getItem(LOCAL_TEMPLATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LocalTemplateDraft>;
    if (typeof parsed.norwegianBody !== 'string' || typeof parsed.englishBody !== 'string' || typeof parsed.updatedAt !== 'number') return null;
    return parsed as LocalTemplateDraft;
  } catch {
    return null;
  }
};

const writeLocalTemplateDraft = (norwegianBody: string, englishBody: string, updatedAt = Date.now()): void => {
  window.localStorage.setItem(LOCAL_TEMPLATE_KEY, JSON.stringify({ norwegianBody, englishBody, updatedAt }));
};

const fileToRows = async (file: File): Promise<ParsedMmcData> => {
  if (file.name.toLocaleLowerCase('nb-NO').endsWith('.csv')) return parseMmcCsv(await file.text());
  if (!file.name.toLocaleLowerCase('nb-NO').endsWith('.xlsx')) {
    throw new Error('Velg en .xlsx- eller CSV-fil. .xls støttes ikke.');
  }
  return parseMmcRows(await readSheet(file));
};

const prepare = (group: MmcParticipant, norwegianBody: string, englishBody: string): PreparedMmcEmail => {
  const markup = buildLocalizedMmcMarkup(group, norwegianBody, englishBody);
  return {
    to: group.email,
    subject: getMmcSubject(group),
    text: markupToPlainText(markup, group.language),
    html: buildMmcEmailHtml(markup, LOGO_URL, group.language),
    sourceRows: group.sourceRows,
    language: group.language,
    outcome: group.outcome,
    participantCount: group.participantCount,
    recipientName: group.recipientName,
  };
};

const MmcEmailPocPanel: React.FC = () => {
  const [initialDraft] = useState<LocalTemplateDraft | null>(() => readLocalTemplateDraft());
  const [data, setData] = useState<ParsedMmcData | null>(null);
  const [fileName, setFileName] = useState('');
  const [norwegianBody, setNorwegianBody] = useState(initialDraft?.norwegianBody ?? NORWEGIAN_BODY);
  const [englishBody, setEnglishBody] = useState(initialDraft?.englishBody ?? ENGLISH_BODY);
  const [templatesReady, setTemplatesReady] = useState(false);
  const [templateDirty, setTemplateDirty] = useState(Boolean(initialDraft));
  const [templateMessage, setTemplateMessage] = useState('Laster lagrede standardtekster…');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [testAddress, setTestAddress] = useState('');
  const [testSent, setTestSent] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [showRaw, setShowRaw] = useState(false);
  const [marketingApproved, setMarketingApproved] = useState(false);

  const groups = useMemo(() => data?.participants ?? [], [data]);
  const previewGroup = groups[Math.min(previewIndex, Math.max(0, groups.length - 1))];
  const marketing = containsMarketingSections(norwegianBody) || containsMarketingSections(englishBody);
  const expectedConfirmation = `SEND ${groups.length}`;
  const durationHours = Math.max(0, (groups.length - 1) * 60 / 3600);
  const counts = useMemo(() => groups.reduce((acc, group) => {
    acc[group.language] += 1; acc[group.outcome] += 1;
    if (group.participantCount > 1 && !group.recipientName) acc.unresolved += 1;
    return acc;
  }, { nb: 0, en: 0, finished: 0, dns: 0, dnf: 0, mixed: 0, unresolved: 0 }), [groups]);
  const previewMarkup = useMemo(() => previewGroup ? buildLocalizedMmcMarkup(previewGroup, norwegianBody, englishBody) : '', [previewGroup, norwegianBody, englishBody]);
  const previewHtml = useMemo(() => previewMarkup ? buildMmcEmailHtml(previewMarkup, LOGO_URL, previewGroup?.language) : '', [previewMarkup, previewGroup]);

  useEffect(() => {
    let cancelled = false;
    const loadSavedTemplate = async () => {
      try {
        const remote = await loadMmcContentTemplate();
        if (cancelled) return;
        const remoteTime = remote?.updatedAt?.getTime() ?? 0;
        const localTime = initialDraft?.updatedAt ?? 0;
        if (remote && remoteTime >= localTime) {
          setNorwegianBody(remote.norwegianBody);
          setEnglishBody(remote.englishBody);
          setLastSavedAt(remote.updatedAt);
          setTemplateDirty(false);
          setTemplateMessage('Lagrede standardtekster er lastet fra RunnersHub.');
        } else if (initialDraft) {
          setTemplateDirty(true);
          setTemplateMessage('Et nyere lokalt utkast er gjenopprettet. Trykk «Lagre standardtekster» for å lagre det i RunnersHub.');
        } else {
          setTemplateMessage('Ingen lagret versjon finnes ennå. Standardtekstene lagres lokalt mens du skriver.');
        }
      } catch {
        if (!cancelled) setTemplateMessage('Kunne ikke hente serverversjonen. Et lokalt utkast lagres fortsatt automatisk i denne nettleseren.');
      } finally {
        if (!cancelled) setTemplatesReady(true);
      }
    };
    loadSavedTemplate();
    return () => { cancelled = true; };
  }, [initialDraft]);

  useEffect(() => {
    if (!templatesReady) return;
    try { writeLocalTemplateDraft(norwegianBody, englishBody); } catch { /* Browser storage can be disabled. */ }
  }, [englishBody, norwegianBody, templatesReady]);

  const resetApproval = () => { setTestSent(false); setMarketingApproved(false); setConfirmation(''); setStatus(null); };
  const saveStandardTexts = async () => {
    setBusy(true); setTemplateMessage('Lagrer standardtekstene…');
    try {
      await saveMmcContentTemplate(norwegianBody, englishBody);
      const savedAt = new Date();
      writeLocalTemplateDraft(norwegianBody, englishBody, savedAt.getTime());
      setLastSavedAt(savedAt);
      setTemplateDirty(false);
      setTemplateMessage('Standardtekstene er lagret i RunnersHub og lokalt i nettleseren.');
    } catch (error) {
      setTemplateMessage(error instanceof Error ? `Kunne ikke lagre i RunnersHub: ${error.message}` : 'Kunne ikke lagre standardtekstene i RunnersHub.');
    } finally { setBusy(false); }
  };

  const restoreBuiltInTexts = () => {
    setNorwegianBody(NORWEGIAN_BODY);
    setEnglishBody(ENGLISH_BODY);
    setTemplateDirty(true);
    setTemplateMessage('De innebygde tekstene er hentet frem. Trykk «Lagre standardtekster» dersom de skal erstatte den lagrede versjonen.');
    resetApproval();
  };
  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true); setStatus(null);
    try {
      const parsed = await fileToRows(file);
      setData(parsed); setFileName(file.name); setPreviewIndex(0); resetApproval();
      setStatus(`Leste ${parsed.participants.length} mottakergrupper fra ${file.name}.`);
    } catch (error) { setData(null); setStatus(error instanceof Error ? error.message : 'Kunne ikke lese filen.'); }
    finally { setBusy(false); event.target.value = ''; }
  };
  const sendTest = async () => {
    if (!previewGroup || !/^\S+@\S+\.\S+$/.test(testAddress.trim())) return;
    setBusy(true); setStatus(null);
    try {
      const email = prepare(previewGroup, norwegianBody, englishBody);
      const id = await queueMmcTestEmail({ ...email, to: testAddress.trim().toLocaleLowerCase('nb-NO'), subject: `[TEST] ${email.subject}` }, marketing);
      setTestSent(true); setStatus(`Testmelding er lagt i kø (ID ${id}). Kontroller innboks og søppelpost før full utsending.`);
    } catch (error) { setStatus(error instanceof Error ? error.message : 'Testmeldingen kunne ikke kølegges.'); }
    finally { setBusy(false); }
  };
  const queueAll = async () => {
    if (!data || (marketing && !marketingApproved) || !testSent || confirmation !== expectedConfirmation) return;
    setBusy(true); setProgress(0); setStatus(null);
    try {
      const emails = groups.map((group) => prepare(group, norwegianBody, englishBody));
      const batchId = await queueMmcEmails(emails, { marketing, onProgress: (done) => setProgress(done) });
      setStatus(`Alle ${emails.length} mottakergruppene er lagt kontrollert i kø. Batch: ${batchId}.`); setConfirmation('');
    } catch (error) { setStatus(error instanceof Error ? error.message : 'Utsendingen kunne ikke kølegges.'); }
    finally { setBusy(false); }
  };

  return <Stack spacing={2}>
    <Box><Typography variant="h4">MMC e-postutsending (PoC)</Typography><Typography color="text.secondary">Én melding per e-postadresse, tilpasset språk, deltakergruppe og resultatstatus.</Typography></Box>
    <Alert severity="info">Avsender: <strong>post@e-post.mmctrail.no</strong> · Reply-To: <strong>post@mmctrail.no</strong> · Brevo · minst 60 sekunder mellom meldinger.</Alert>
    {marketing && <Stack spacing={1}>
      <Alert severity="info">Meldingene får synlig avmeldingslenke og ett-klikk-avmelding. Avmelding gjelder bare fremtidige MMC-nyheter og tilbud, ikke nødvendig deltakerinformasjon.</Alert>
      <Alert severity="warning" action={<Button color="inherit" size="small" startIcon={<Trash2 size={16}/>} onClick={() => { setNorwegianBody(removeMarketingSections(norwegianBody)); setEnglishBody(removeMarketingSections(englishBody)); setTemplateDirty(true); resetApproval(); }}>Fjern markedsføring</Button>}>
        Tekstene inneholder 2027-påmelding og/eller rabattkode. Behold innholdet ved å bekrefte valget nedenfor, eller fjern avsnittene.
      </Alert>
      <FormControlLabel
        control={<Switch color="warning" checked={marketingApproved} onChange={(_, checked) => { setMarketingApproved(checked); setConfirmation(''); }}/>}
        label="Jeg bekrefter at hele teksten, inkludert 2027-påmelding og rabattkode, skal beholdes i denne utsendingen."
      />
    </Stack>}
    {status && <Alert severity={status.startsWith('Kunne') || status.includes('kunne ikke') ? 'error' : 'success'}>{status}</Alert>}

    <Card variant="outlined"><CardContent><Stack spacing={2}>
      <Typography variant="h6">1. Mottakere og segmenter</Typography>
      <Button component="label" variant="contained" startIcon={<Upload size={18}/>} disabled={busy} sx={{ alignSelf: 'flex-start' }}>Velg Excel eller CSV<input hidden type="file" accept=".xlsx,.csv" onChange={handleFile}/></Button>
      {fileName && <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip label={fileName}/><Chip color="success" label={`${groups.length} mottakergrupper`}/><Chip label={`${data?.duplicateCount ?? 0} ekstra deltakerrader samlet`}/><Chip color={(data?.rejected.length ?? 0) ? 'warning' : 'default'} label={`${data?.rejected.length ?? 0} avviste rader`}/>
        <Chip label={`Norsk ${counts.nb}`}/><Chip label={`Engelsk ${counts.en}`}/><Chip label={`Fullført ${counts.finished}`}/><Chip label={`DNS ${counts.dns}`}/><Chip label={`DNF ${counts.dnf}`}/><Chip label={`Blandet ${counts.mixed}`}/>
      </Stack>}
      {counts.unresolved > 0 && <Alert severity="warning">{counts.unresolved} flerperson-adresser har ingen tydelig påmelder i deltakerlisten og får generell tiltale. Kontroller disse i forhåndsvisningen.</Alert>}
      {!!groups.length && <Box sx={{ overflowX: 'auto' }}><Table size="small"><TableHead><TableRow><TableCell>Adressat</TableCell><TableCell>Deltakere</TableCell><TableCell>Språk</TableCell><TableCell>Status</TableCell><TableCell>E-post</TableCell></TableRow></TableHead><TableBody>{groups.slice(0, 10).map((group) => <TableRow key={group.email}><TableCell>{group.recipientName || 'Generell tiltale'}</TableCell><TableCell>{group.name}</TableCell><TableCell>{group.language === 'nb' ? 'Norsk' : 'English'}</TableCell><TableCell>{outcomeLabel(group.outcome)}</TableCell><TableCell>{group.email}</TableCell></TableRow>)}</TableBody></Table><Typography variant="caption" color="text.secondary">Viser de første ti mottakergruppene. Originalfilen endres ikke.</Typography></Box>}
    </Stack></CardContent></Card>

    <Card variant="outlined"><CardContent><Stack spacing={2}>
      <Typography variant="h6">2. Norske og engelske meldingstekster</Typography>
      <Alert severity="info">Tiltale, statusinnledning og emnefelt genereres automatisk. Resten kan redigeres her. Flettefelt: {'{{recipientFirstName}}'}, {'{{name}}'}, {'{{distanceShortName}}'}, {'{{bib}}'} og {'{{participantSummary}}'}.</Alert>
      <Alert severity={templateMessage.startsWith('Kunne ikke') ? 'warning' : 'info'}>
        {templateMessage}{lastSavedAt ? ` Sist lagret ${lastSavedAt.toLocaleString('nb-NO')}.` : ''}
      </Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button variant="contained" startIcon={<SaveIcon size={17}/>} onClick={saveStandardTexts} disabled={busy || !templatesReady || !templateDirty}>Lagre standardtekster</Button>
        <Button variant="outlined" startIcon={<RotateCcw size={17}/>} onClick={restoreBuiltInTexts} disabled={busy || !templatesReady}>Gjenopprett innebygde tekster</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">Endringer lagres automatisk som lokalt utkast. Knappen lagrer dem også sentralt, slik at de følger deg til andre nettlesere og overlever nye deployer.</Typography>
      <TextField label="Norsk fellesdel" fullWidth multiline minRows={16} value={norwegianBody} onChange={(e) => { setNorwegianBody(e.target.value); setTemplateDirty(true); resetApproval(); }}/>
      <TextField label="English common section" fullWidth multiline minRows={16} value={englishBody} onChange={(e) => { setEnglishBody(e.target.value); setTemplateDirty(true); resetApproval(); }}/>
      {!!previewGroup && <><Divider/><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <TextField select SelectProps={{ native: true }} label="Forhåndsvis mottakergruppe" value={previewIndex} onChange={(e) => setPreviewIndex(Number(e.target.value))} sx={{ minWidth: 360 }}>{groups.map((group, index) => <option key={group.email} value={index}>{group.recipientName || group.name} – {outcomeLabel(group.outcome)} – {group.language.toUpperCase()}</option>)}</TextField>
        <FormControlLabel control={<Switch checked={showRaw} onChange={(_, checked) => setShowRaw(checked)}/>} label="Vis ren tekst"/>
      </Stack>
      <Typography variant="subtitle2">Emne: {getMmcSubject(previewGroup)}</Typography>
      {showRaw ? <Box component="pre" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: 'background.default', color: 'text.primary', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>{markupToPlainText(previewMarkup, previewGroup.language)}</Box> : <Box component="iframe" title="E-postforhåndsvisning" sandbox="" srcDoc={previewHtml} sx={{ width: '100%', height: 720, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}/>}</>}
    </Stack></CardContent></Card>

    <Card variant="outlined"><CardContent><Stack spacing={2}>
      <Typography variant="h6">3. Test og kontrollert kølegging</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}><TextField label="Send valgt forhåndsvisning som test til" type="email" value={testAddress} onChange={(e) => { setTestAddress(e.target.value); setTestSent(false); }} sx={{ minWidth: 360 }}/><Button variant="outlined" startIcon={<Send size={17}/>} disabled={busy || !previewGroup || !/^\S+@\S+\.\S+$/.test(testAddress.trim())} onClick={sendTest}>Send test</Button></Stack>
      {testSent && <Alert icon={<ShieldCheck/>} severity="success">Test er kølagt. Kontroller både HTML, ren tekst, språk, tiltale og statusordlyd.</Alert>}
      <Typography>Estimert sendetid: <strong>{durationHours.toFixed(1)} timer</strong> for {groups.length} mottakergrupper.</Typography>
      <TextField label={`Skriv ${expectedConfirmation} for å bekrefte`} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} disabled={!testSent || (marketing && !marketingApproved) || !groups.length}/>
      {busy && progress > 0 && <Typography>Kølegger: {progress} / {groups.length}</Typography>}
      <Button color="error" variant="contained" size="large" disabled={busy || (marketing && !marketingApproved) || !testSent || confirmation !== expectedConfirmation} onClick={queueAll}>Legg {groups.length} meldinger i MMC-køen</Button>
      <Typography variant="caption" color="text.secondary">Ingen meldinger opprettes før denne knappen brukes. SMTP-agenten sender dem deretter gradvis.</Typography>
    </Stack></CardContent></Card>
    {busy && <CircularProgress size={24}/>}
  </Stack>;
};

export default MmcEmailPocPanel;
